// Generic status pill. Grammar extracted verbatim from
// components/DeviceStatusPill.tsx: bg = colour @ 16% alpha,
// fg = full colour, 6px dot. Every tone reads from theme tokens
// in index.css so dark/light flip works automatically.

import type { ReactNode } from "react";

export type PillTone = "ok" | "warn" | "crit" | "info" | "neutral";

interface PillProps {
  tone: PillTone;
  children: ReactNode;
  dot?: boolean;
  title?: string;
}

const TONE_FG: Record<PillTone, string> = {
  ok: "var(--ok)",
  warn: "var(--warn)",
  crit: "var(--crit)",
  info: "var(--accent)",
  neutral: "var(--text-dim)",
};

// 16% alpha background — hardcoded RGB triplets matching the theme
// tokens above (CSS var alpha math via color-mix isn't in
// our Vite/PostCSS pipeline yet). Update both columns if you change
// the theme.
const TONE_BG: Record<PillTone, string> = {
  ok: "rgba(74, 222, 128, 0.16)",
  warn: "rgba(251, 191, 36, 0.16)",
  crit: "rgba(248, 113, 113, 0.16)",
  info: "rgba(94, 234, 212, 0.16)",
  neutral: "rgba(141, 150, 160, 0.16)",
};

export function Pill({ tone, children, dot = true, title }: PillProps) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 999,
        background: TONE_BG[tone],
        color: TONE_FG[tone],
        fontSize: "0.78rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: TONE_FG[tone],
          }}
        />
      )}
      {children}
    </span>
  );
}
